import { createContext, useContext, useEffect, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import { useAuth } from 'context/AuthContext';
import { safeJSONParse } from 'utils/helpers/safeParsing';

interface VendorsContextValue {
    showSpinner: boolean;
    setShowSpinner: Dispatch<SetStateAction<boolean>>;
    vendorsOrder: string;
    setVendorsOrder: Dispatch<SetStateAction<string>>;
    vendorsDesc: boolean;
    setVendorsDesc: Dispatch<SetStateAction<boolean>>;
    listResetNonce: number;
    triggerListReset: () => void;
    listRefreshNonce: number;
    triggerListRefresh: () => void;
}

const VendorsContext = createContext<VendorsContextValue | null>(null);

export const useVendors = (): VendorsContextValue => useContext(VendorsContext) as VendorsContextValue;

interface VendorsProviderProps {
    children: ReactNode;
}

export const VendorsProvider = ({ children }: VendorsProviderProps) => {
    const { auth } = useAuth();
    const orderName = `voltox-${auth.user.ID}-vendors-order`;
    const descName = `voltox-${auth.user.ID}-vendors-desc`;

    const [showSpinner, setShowSpinner] = useState(false);
    const [listResetNonce, setListResetNonce] = useState(0);
    const [listRefreshNonce, setListRefreshNonce] = useState(0);
    const [vendorsOrder, setVendorsOrder] = useState(safeJSONParse(sessionStorage.getItem(orderName)) || '');
    const [vendorsDesc, setVendorsDesc] = useState(safeJSONParse(sessionStorage.getItem(descName)) === true);

    const triggerListReset = () => setListResetNonce((nonce) => nonce + 1);
    const triggerListRefresh = () => setListRefreshNonce((nonce) => nonce + 1);

    useEffect(() => {
        sessionStorage.setItem(orderName, JSON.stringify(vendorsOrder));
    }, [orderName, vendorsOrder]);

    useEffect(() => {
        sessionStorage.setItem(descName, JSON.stringify(vendorsDesc));
    }, [descName, vendorsDesc]);

    return (
        <VendorsContext.Provider
            value={{
                showSpinner,
                setShowSpinner,
                vendorsOrder,
                setVendorsOrder,
                vendorsDesc,
                setVendorsDesc,
                listResetNonce,
                triggerListReset,
                listRefreshNonce,
                triggerListRefresh,
            }}
        >
            {children}
        </VendorsContext.Provider>
    );
};
